<template>
    <div class="space-y-4">
        <div class="sticky top-0 z-10 rounded-2xl border border-base-200/80 bg-base-100/95 p-4 shadow-sm backdrop-blur">
            <div class="flex flex-col gap-3">
                <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                        <p class="text-sm font-semibold text-base-content">{{ activePanelMeta.title }}</p>
                        <p class="mt-1 text-xs leading-5 text-base-content/55">{{ activePanelMeta.description }}</p>
                    </div>
                    <button
                        class="btn btn-sm rounded-xl border border-base-300 bg-base-100 px-3 shadow-none hover:border-primary/30 hover:bg-primary/5"
                        type="button"
                        @click="toggleDetail"
                    >
                        {{ detail ? '紧凑模式' : '详细模式' }}
                    </button>
                </div>

                <div class="flex flex-wrap items-center gap-2">
                    <button
                        v-for="panel in panels"
                        :key="panel.key"
                        class="btn btn-xs rounded-xl border px-3 shadow-none"
                        :class="activePanel === panel.key
                            ? 'btn-primary border-primary text-primary-content'
                            : 'border-base-300 bg-base-100 hover:border-primary/30 hover:bg-primary/5'"
                        type="button"
                        @click="changePanel(panel.key)"
                    >
                        {{ panel.shortLabel }}
                    </button>
                </div>
            </div>
        </div>

        <div class="rounded-[24px] border border-base-200/80 bg-base-100 p-3 md:p-4">
            <div v-if="isLoading" class="flex min-h-[220px] items-center justify-center">
                <span class="loading loading-spinner loading-lg text-primary"></span>
            </div>

            <div v-else>
                <BatchSettings
                    v-if="activePanel === 'batch-settings'"
                    :detail="detail"
                    :isLoading="isLoading"
                    @loading-start="handleLoadingStart"
                    @loading-end="handleLoadingEnd"
                />

                <TagEditor
                    v-else-if="activePanel === 'tag-editor'"
                    :detail="detail"
                    :isLoading="isLoading"
                    :checkedData="checkedData"
                    :filePath="filePath"
                    @loading-start="handleLoadingStart"
                    @loading-end="handleLoadingEnd"
                />

                <FileOperations
                    v-else-if="activePanel === 'file-operations'"
                    :detail="detail"
                    :isLoading="isLoading"
                    :checkedData="checkedData"
                    :filePath="filePath"
                    @loading-start="handleLoadingStart"
                    @loading-end="handleLoadingEnd"
                />

                <MediaProcessor
                    v-else-if="activePanel === 'media-processor'"
                    :detail="detail"
                    :isLoading="isLoading"
                    :checkedData="checkedData"
                    :filePath="filePath"
                    :userRole="getUserRole"
                    @loading-start="handleLoadingStart"
                    @loading-end="handleLoadingEnd"
                />

                <TextProcessor
                    v-else-if="activePanel === 'text-processor'"
                    :detail="detail"
                    :isLoading="isLoading"
                    :checkedData="checkedData"
                    :filePath="filePath"
                    @loading-start="handleLoadingStart"
                    @loading-end="handleLoadingEnd"
                />

                <FileIndexer
                    v-else-if="activePanel === 'file-indexer'"
                    :detail="detail"
                    :isLoading="isLoading"
                    :checkedData="checkedData"
                    :filePath="filePath"
                    :userRole="getUserRole"
                    @loading-start="handleLoadingStart"
                    @loading-end="handleLoadingEnd"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import BatchSettings from '@/components/batch/BatchSettings.vue'
import TagEditor from '@/components/batch/TagEditor.vue'
import FileOperations from '@/components/batch/FileOperations.vue'
import TextProcessor from '@/components/batch/TextProcessor.vue'
import MediaProcessor from '@/components/batch/MediaProcessor.vue'
import FileIndexer from '@/components/batch/FileIndexer.vue'

const PANEL_CATALOG = [
    {
        key: 'tag-editor',
        shortLabel: '批量编辑',
        title: '批量编辑',
        description: '集中编辑已勾选文件的标签字段、封面和常见元数据。'
    },
    {
        key: 'file-operations',
        shortLabel: '整理目录',
        title: '整理目录',
        description: '对已选文件执行整理、归类、重命名等目录级操作。'
    },
    {
        key: 'media-processor',
        shortLabel: '抓取资源',
        title: '抓取资源',
        description: '处理封面、歌词与媒体资源抓取，适合补齐音频资料。'
    },
    {
        key: 'batch-settings',
        shortLabel: '更多设置',
        title: '更多设置',
        description: '查看和调整批量处理相关的公共设置项。'
    },
    {
        key: 'text-processor',
        shortLabel: '文本处理',
        title: '文本处理',
        description: '执行文本替换、清洗和批量字段规范化处理。'
    },
    {
        key: 'file-indexer',
        shortLabel: '索引工具',
        title: '索引工具',
        description: '用于索引刷新、扫描辅助与文件层级整理支持。'
    }
]

export default {
    name: 'HomeCloudBatchPanel',
    components: {
        BatchSettings,
        TagEditor,
        FileOperations,
        TextProcessor,
        MediaProcessor,
        FileIndexer
    },
    props: {
        panelKey: {
            type: String,
            default: 'tag-editor'
        }
    },
    data() {
        return {
            detail: false,
            isLoading: false,
            activePanel: 'tag-editor',
            panels: PANEL_CATALOG
        }
    },
    computed: {
        ...mapGetters(['geFullPath', 'getUserRole']),
        filePath() {
            return this.geFullPath
        },
        checkedData() {
            return this.$store.state.common.checkedData
        },
        activePanelMeta() {
            return this.panels.find((panel) => panel.key === this.activePanel) || this.panels[0]
        }
    },
    watch: {
        panelKey: {
            immediate: true,
            handler(value) {
                if (!value) {
                    this.activePanel = 'tag-editor'
                    return
                }
                this.activePanel = value
            }
        }
    },
    mounted() {
        const savedDetail = window.localStorage.getItem('detail')
        if (savedDetail) {
            this.detail = savedDetail === 'true'
        }
    },
    methods: {
        toggleDetail() {
            this.detail = !this.detail
            window.localStorage.setItem('detail', this.detail ? 'true' : 'false')
        },
        changePanel(panelKey) {
            this.activePanel = panelKey
            this.$emit('change-panel', panelKey)
        },
        handleLoadingStart() {
            this.isLoading = true
        },
        handleLoadingEnd() {
            this.isLoading = false
        }
    }
}
</script>
